import { useEffect } from 'react';
import { Sparkles, TrendingUp, Clock, Loader2 } from 'lucide-react';
import GameCard from '../components/game/GameCard';
import GameGrid from '../components/game/GameGrid';
import GameCarousel from '../components/game/GameCarousel';
import { useGamesStore } from '../store/useGamesStore';
import { LINE_GRADIENT } from '../constants/gradients';
import { useAnalytics } from '../hooks/useAnalytics';

export default function HomePage() {
  const { trackEvent } = useAnalytics();
  
  const {
    games,
    isLoadingCatalog,
    catalogError,
    hasLoadedCatalog,
    fetchCatalog,
    getFeaturedGames,
    getTrendingGames,
    getRecentGames,
    getGamesByCategory,
    getSections,
  } = useGamesStore();

  // Fetch catalog on mount
  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  // Track games loaded
  useEffect(() => {
    if (hasLoadedCatalog && games.length > 0) {
      trackEvent('webplay.games_loaded', {
        games_count: games.length,
        is_refresh: false
      });
    }
  }, [hasLoadedCatalog, games.length, trackEvent]);

  const featuredGames = getFeaturedGames(6); // Get more games for carousel
  const recentGames = getRecentGames(4);
  const trendingGames = getTrendingGames(4);
  const allGames = games;
  const sections = getSections();
  
  // Get additional sections (popular, recommended) that aren't already displayed
  const additionalSections = sections.filter(
    s => s.type !== 'featured' && 
         s.type !== 'trending' && 
         s.type !== 'new' && 
         s.type !== 'category' &&
         s.games.length > 0
  );

  // Loading skeleton component
  const GameCardSkeleton = () => (
    <div className="card overflow-hidden animate-pulse">
      <div className="aspect-[3/4] bg-gray-200 dark:bg-gray-700" />
      <div className="p-4 space-y-2">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
      </div>
    </div>
  );

  const GridSkeleton = ({ count = 4 }: { count?: number }) => (
    <GameGrid>
      {Array.from({ length: count }).map((_, i) => (
        <GameCardSkeleton key={i} />
      ))}
    </GameGrid>
  );

  // Error state
  if (catalogError && !hasLoadedCatalog) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-red-500 mb-4">Failed to load games</h2>
        <p className="text-muted mb-4">{catalogError}</p>
        <button
          onClick={() => fetchCatalog({}, true)}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* SVG Gradient Definition */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: 'rgb(252, 131, 2)', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#FF00CF', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
      </svg>

      {/* Featured Games */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <Sparkles 
            className="w-7 h-7" 
            style={{
              stroke: 'url(#icon-gradient)',
              fill: 'url(#icon-gradient)',
            }}
          />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Featured Games</h2>
        </div>
        {isLoadingCatalog ? (
          <GridSkeleton count={3} />
        ) : featuredGames.length > 0 ? (
          <GameCarousel games={featuredGames} />
        ) : null}
      </section>

      {/* Trending This Week */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <TrendingUp 
            className="w-7 h-7" 
            style={{
              stroke: 'url(#icon-gradient)',
            }}
          />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Trending This Week</h2>
        </div>
        {isLoadingCatalog ? (
          <GridSkeleton count={4} />
        ) : trendingGames.length > 0 ? (
          <GameGrid>
            {trendingGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : null}
      </section>

      {/* Recently Added */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <Clock 
            className="w-7 h-7" 
            style={{
              stroke: 'url(#icon-gradient)',
            }}
          />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Recently Added</h2>
        </div>
        {isLoadingCatalog ? (
          <GridSkeleton count={4} />
        ) : recentGames.length > 0 ? (
          <GameGrid>
            {recentGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : null}
      </section>

      {/* Additional Server Sections (Popular, Recommended, etc.) */}
      {additionalSections.map((section) => (
        <section key={section.type}>
          <div className="flex items-center gap-3 mb-6">
            <Sparkles 
              className="w-7 h-7" 
              style={{
                stroke: 'url(#icon-gradient)',
                fill: 'url(#icon-gradient)',
              }}
            />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{section.title}</h2>
          </div>
          <GameGrid>
            {section.games.slice(0, 4).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        </section>
      ))}

      {/* All Games */}
      <section className="min-h-[600px]">
        <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
          All Games
        </h2>
        {isLoadingCatalog ? (
          <GridSkeleton count={8} />
        ) : allGames.length > 0 ? (
          <GameGrid>
            {allGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-muted">No games found</p>
          </div>
        )}
      </section>

      {/* Loading indicator when refreshing */}
      {isLoadingCatalog && hasLoadedCatalog && (
        <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      )}
    </div>
  );
}
